import { useState, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, ChevronDown, Upload, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useResponsive } from '../hooks/useResponsive';
import { bonndleAuthApi } from '../services/bonndleAuthApi';
import VerificationResultDrawer from '../components/auth/VerificationResultDrawer';

// Check if user is from Nigeria based on country or phone code
const isNigeriaUser = (user, userProfile) => {
  // Check country field (could be 'NG' or 'Nigeria')
  const country = user?.country || userProfile?.country || '';
  if (country === 'NG' || country.toLowerCase() === 'nigeria') {
    return true;
  }
  // Check phone_number_code field ('+234' for Nigeria)
  const phoneCode = user?.phone_number_code || userProfile?.phoneNumberCode || '';
  if (phoneCode === '+234') {
    return true;
  }
  // Check if phone number starts with +234
  const phone = user?.phone || userProfile?.phone || '';
  if (phone.startsWith('+234') || phone.startsWith('234')) {
    return true;
  }
  return false;
};

// Identity type options based on country
const getIdentityTypes = (isNigeria) => {
  const types = [
    { value: '', label: 'Select identity type' },
  ];

  if (isNigeria) {
    // Nigeria: Only BVN
    types.push({ value: 'bvn', label: 'BVN (Bank Verification Number)' });
  } else {
    // Other countries: Passport and Driver's License
    types.push(
      { value: 'passport', label: 'Passport' },
      { value: 'drivers_license', label: "Driver's License" },
    );
  }

  return types;
};

export default function IdentityVerificationPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { enterApp, userProfile, user, fetchUserData } = useAuth();
  const { isDesktop } = useResponsive();
  const fileInputRef = useRef(null);

  // Check if coming from menu (not signup flow)
  const fromMenu = location.state?.fromMenu || false;

  // Detect if user is from Nigeria
  const isNigeria = useMemo(() => isNigeriaUser(user, userProfile), [user, userProfile]);
  const identityTypes = useMemo(() => getIdentityTypes(isNigeria), [isNigeria]);

  const [verificationType, setVerificationType] = useState('');
  const [verificationNumber, setVerificationNumber] = useState('');
  const [uploadedFile, setUploadedFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showResultDrawer, setShowResultDrawer] = useState(false);
  const [verificationSuccess, setVerificationSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Check if user is already verified
  const isAlreadyVerified = user?.verification_status === 'VERIFIED';

  const handleBack = () => {
    if (fromMenu) {
      navigate('/menu');
    } else {
      navigate('/signup-success');
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
      if (!validTypes.includes(file.type)) {
        setErrorMessage('Please upload a valid image (JPG, PNG) or PDF file');
        return;
      }
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setErrorMessage('File size must be less than 5MB');
        return;
      }
      setUploadedFile(file);
      setErrorMessage('');
    }
  };

  const handleRemoveFile = () => {
    setUploadedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleVerify = async () => {
    setIsLoading(true);
    setErrorMessage('');

    try {
      if (verificationType === 'bvn') {
        // Call BVN verification endpoint
        await bonndleAuthApi.verifyBVN(verificationNumber);
      } else if ((verificationType === 'passport' || verificationType === 'drivers_license') && uploadedFile) {
        // For document upload (passport or driver's license)
        await bonndleAuthApi.uploadVerificationDocument(uploadedFile);
      }

      setVerificationSuccess(true);
      setShowResultDrawer(true);
    } catch (error) {
      console.error('Verification failed:', error);

      // Check if already verified (API returns this when user tries to verify again)
      const errorMsg = error.message || '';
      if (errorMsg.toLowerCase().includes('already verified')) {
        // Refresh user data to get updated verification status
        try {
          await fetchUserData();
        } catch (e) {
          console.error('Failed to refresh user data:', e);
        }
        // Show success since they're already verified
        setVerificationSuccess(true);
        setShowResultDrawer(true);
      } else {
        setVerificationSuccess(false);
        setErrorMessage(errorMsg || 'Verification failed. Please try again.');
        setShowResultDrawer(true);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleProceed = async () => {
    setShowResultDrawer(false);
    if (verificationSuccess) {
      // Refresh user data to get updated verification status
      try {
        await fetchUserData();
      } catch (e) {
        console.error('Failed to refresh user data:', e);
      }

      if (fromMenu) {
        // Navigate back to menu - user will see updated verification status
        navigate('/menu');
      } else {
        // From signup flow - continue to profile
        enterApp();
        navigate('/profile', { state: { fromSignup: true, fromVerification: true } });
      }
    }
  };

  const handleTryAgain = () => {
    setShowResultDrawer(false);
    setVerificationNumber('');
    setUploadedFile(null);
  };

  const isValid = verificationType && (
    (verificationType === 'bvn' && verificationNumber.length === 11) ||
    (verificationType === 'passport' && uploadedFile) ||
    (verificationType === 'drivers_license' && uploadedFile)
  );

  return (
    <div className="min-h-screen min-h-dvh flex flex-col bg-white">
      <div className={`flex-1 flex flex-col ${isDesktop ? 'max-w-md mx-auto w-full' : ''}`}>
        {/* Header */}
        <header className="sticky top-0 bg-white z-10 flex items-center gap-4 px-4 pb-3 pt-[calc(0.75rem+env(safe-area-inset-top))] border-b border-gray-100">
          <button
            onClick={handleBack}
            className="p-2 text-gray-600 hover:bg-gray-100 rounded-full"
          >
            <ArrowLeft size={24} />
          </button>
          <h1 className="text-xl font-bold">Identity Verification</h1>
        </header>

        {/* Content */}
        <div className="flex-1 px-6 py-6 flex flex-col">
          {/* Logo Partnership */}
          <div className="flex items-center justify-center gap-4 mb-6">
            <img
              src="/logo/bonndleLogoIcon_Indigo.svg"
              alt="Bonndle"
              className="w-12 h-12"
            />
            <div className="h-10 w-px bg-gray-300" />
            <img
              src="/Smile ID Logoset/PNG/Smile_Logo_Main_Color_2x.png"
              alt="SmileID"
              className="h-10 object-contain"
            />
          </div>

          {/* Already Verified Message */}
          {isAlreadyVerified ? (
            <div className="flex-1 flex flex-col items-center justify-center text-center">
              <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">Already Verified</h2>
              <p className="text-gray-600 mb-8">
                Your identity has already been verified. No further action is needed.
              </p>
              <button
                onClick={() => navigate(fromMenu ? '/menu' : '/home')}
                className="w-full max-w-xs py-4 rounded-2xl font-bold text-lg bg-indigo-600 text-white hover:bg-indigo-700 active:scale-[0.98] transition-all"
              >
                {fromMenu ? 'Back to Menu' : 'Continue to Home'}
              </button>
            </div>
          ) : (
            <>
          {/* Partnership Message */}
          <div className="bg-indigo-50 border border-indigo-100 rounded-2xl p-4 mb-6">
            <p className="text-gray-700 text-sm leading-relaxed text-center">
              To help us meet regulatory requirements and platform safety policies, we've partnered with our friends at <span className="font-semibold text-indigo-600">SmileID</span> to verify your government-issued ID.
            </p>
          </div>

          {/* Verification Form */}
          <div className="space-y-4">
            {/* Identity Type Dropdown */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Identity Type
              </label>
              <div className="relative">
                <select
                  value={verificationType}
                  onChange={(e) => {
                    setVerificationType(e.target.value);
                    setVerificationNumber('');
                    setUploadedFile(null);
                    setErrorMessage('');
                  }}
                  className="input appearance-none pr-10"
                >
                  {identityTypes.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <ChevronDown size={18} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              </div>
            </div>

            {/* BVN Input */}
            {verificationType === 'bvn' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  BVN Number
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={11}
                  value={verificationNumber}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '');
                    setVerificationNumber(value);
                    setErrorMessage('');
                  }}
                  placeholder="Enter your 11-digit BVN"
                  className="input"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {verificationNumber.length}/11 digits
                </p>
              </div>
            )}

            {/* Document Upload for Passport and Driver's License */}
            {(verificationType === 'passport' || verificationType === 'drivers_license') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Upload {verificationType === 'passport' ? 'Passport' : "Driver's License"}
                </label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/jpg,application/pdf"
                  onChange={handleFileSelect}
                  className="hidden"
                />

                {uploadedFile ? (
                  <div className="border-2 border-indigo-200 bg-indigo-50 rounded-2xl p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Upload size={20} className="text-indigo-600" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {uploadedFile.name}
                        </p>
                        <p className="text-xs text-gray-500">
                          {(uploadedFile.size / 1024).toFixed(1)} KB
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={handleRemoveFile}
                      className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-colors"
                    >
                      <X size={20} />
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full border-2 border-dashed border-gray-300 rounded-2xl p-8 flex flex-col items-center gap-3 hover:border-indigo-400 hover:bg-indigo-50/50 transition-colors"
                  >
                    <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center">
                      <Upload size={24} className="text-gray-400" />
                    </div>
                    <div className="text-center">
                      <p className="text-sm font-medium text-gray-700">
                        Tap to upload your {verificationType === 'passport' ? 'passport' : "driver's license"}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        JPG, PNG or PDF (max 5MB)
                      </p>
                    </div>
                  </button>
                )}
              </div>
            )}

            {/* Error Message */}
            {errorMessage && !showResultDrawer && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-3">
                <p className="text-red-600 text-sm">{errorMessage}</p>
              </div>
            )}
          </div>

          <div className="flex-1" />

          {/* Verify Button */}
          <button
            type="button"
            onClick={handleVerify}
            disabled={!isValid || isLoading}
            className={`w-full py-4 rounded-2xl font-bold text-lg transition-all mt-6 ${
              isValid && !isLoading
                ? 'bg-indigo-600 text-white hover:bg-indigo-700 active:scale-[0.98]'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
          >
            {isLoading ? (
              <span className="flex items-center justify-center gap-2">
                <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Verifying...
              </span>
            ) : (
              'Verify Identity'
            )}
          </button>

            </>
          )}
        </div>

        {/* Fixed Bottom Bar - Update Profile Button */}
        <div className="sticky bottom-0 bg-white px-6 py-4 pb-[calc(1rem+env(safe-area-inset-bottom))]">
          <button
            type="button"
            onClick={() => navigate('/profile', { state: { fromSignup: !fromMenu } })}
            className="w-full py-3.5 rounded-xl font-semibold text-indigo-600 bg-indigo-50 hover:bg-indigo-100 active:scale-[0.98] transition-all"
          >
            Update Profile
          </button>
        </div>
      </div>

      {/* Verification Result Drawer */}
      <VerificationResultDrawer
        isOpen={showResultDrawer}
        isSuccess={verificationSuccess}
        onClose={() => setShowResultDrawer(false)}
        onProceed={verificationSuccess ? handleProceed : handleTryAgain}
      />
    </div>
  );
}
